import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Ruler, Clock, MessageCircle } from "lucide-react";
import { PROJECTS, waLink } from "../data/content";

const FILTERS = [
  { key: "all", label: "Semua", testId: "filter-all" },
  { key: "Mansion", label: "Mansion", testId: "filter-mansion" },
  { key: "Villa Tropis", label: "Villa Tropis", testId: "filter-villa" },
  { key: "Modern Minimal", label: "Modern Minimal", testId: "filter-modern" },
];

export const Portfolio = () => {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const shown = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="portofolio" data-testid="portfolio-section" className="relative py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="font-mono-arch text-[11px] tracking-[0.35em] uppercase text-brass mb-5"
        >
          Portofolio Terpilih
        </motion.p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="font-serif-lux font-light text-3xl sm:text-4xl lg:text-5xl text-bone tracking-tight leading-[1.12]"
          >
            Karya yang berbicara<br />lebih keras dari janji.
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-wrap gap-2"
          >
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={f.testId}
                onClick={() => setFilter(f.key)}
                className={`font-mono-arch text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${
                  filter === f.key
                    ? "border-brass text-gold bg-brass/10"
                    : "border-white/10 text-dim hover:text-ash hover:border-white/25"
                }`}
              >
                {f.label}
              </button>
            ))}
          </motion.div>
        </div>

        <motion.div layout className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {shown.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                data-testid={`portfolio-card-${p.id}`}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
                onClick={() => setActive(p)}
                className={`group relative text-left overflow-hidden border border-white/5 hover:border-[rgba(197,160,89,0.4)] transition-colors duration-500 ${
                  i % 3 === 0 ? "lg:mt-0" : i % 3 === 1 ? "lg:mt-10" : "lg:mt-20"
                }`}
              >
                <div className="relative aspect-[4/5] overflow-hidden clipped-frame bg-surface">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11]/90 via-transparent to-transparent" />
                  <p className="absolute top-4 left-4 font-mono-arch text-[10px] tracking-[0.25em] uppercase text-gold/90 bg-obsidian/60 backdrop-blur-sm px-3 py-1.5">
                    {p.category}
                  </p>
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <h3 className="font-serif-lux text-2xl text-bone">{p.title}</h3>
                    <p className="font-mono-arch text-[10px] tracking-[0.2em] uppercase text-ash mt-2">
                      {p.location} — {p.year}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="portfolio-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8 bg-obsidian/85 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[88vh] overflow-y-auto bg-surface border border-[rgba(197,160,89,0.25)]"
            >
              <button
                data-testid="portfolio-modal-close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-obsidian/70 backdrop-blur-sm text-ash hover:text-gold transition-colors"
                aria-label="Tutup detail proyek"
              >
                <X className="w-5 h-5" strokeWidth={1.6} />
              </button>
              <div className="grid md:grid-cols-2">
                <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[480px]">
                  <img src={active.image} alt={active.title} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="p-8 lg:p-10">
                  <p className="font-mono-arch text-[10px] tracking-[0.3em] uppercase text-brass">{active.category} — {active.year}</p>
                  <h3 className="font-serif-lux text-3xl lg:text-4xl text-bone mt-4">{active.title}</h3>
                  <p className="text-sm text-ash font-light leading-relaxed mt-5">{active.description}</p>
                  <div className="mt-8 space-y-4 border-t border-white/10 pt-6">
                    {[
                      [MapPin, "Lokasi", active.location],
                      [Ruler, "Luas Bangunan", active.area],
                      [Clock, "Durasi Pembangunan", active.duration],
                    ].map(([Icon, label, value]) => (
                      <div key={label} className="flex items-center gap-4">
                        <Icon className="w-4 h-4 text-brass shrink-0" strokeWidth={1.6} />
                        <div>
                          <p className="font-mono-arch text-[9px] tracking-[0.25em] uppercase text-dim">{label}</p>
                          <p className="text-sm text-bone mt-0.5">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <a
                    data-testid="portfolio-modal-whatsapp-btn"
                    href={waLink(`Halo PT Raditya Konstruksi, saya tertarik dengan proyek "${active.title}". Boleh saya tahu lebih lanjut?`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-9 inline-flex items-center gap-3 bg-wa hover:bg-wahover text-obsidian font-mono-arch text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 transition-colors duration-300"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
                    Tanyakan Proyek Ini via WA
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
